import React from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { Employee } from '../../../store/slices/employeeSlice';
import { CountBadge, PersonAvatar } from '../../../components/ui';
import { focusRing, sbg, transition, typeStyle } from '../../../theme/sbgTokens';
import { formatCount, formatNumber } from '../../../utils/format';
import { DensityMode, getEmployeeTeamLabel, jobTitleOf, PositionedOrgNode, SelectVia } from './orgChartModel';
import { DepartmentLabel, DeptDot, HighlightText, viaOf } from './OrgChartBits';

/** Avatars shown in a manager's team strip before the "+N" pill (no overlap, 24px targets 4px apart). */
const GROUPED_PREVIEW = 4;

export interface OrgNodeCardProps {
  item: PositionedOrgNode;
  densityMode: DensityMode;
  isExpanded: boolean;
  isSelected: boolean;
  /** The person matches the search term. */
  isMatch: boolean;
  /** Shown only as the reporting line above a filtered match. */
  isContext: boolean;
  isDragOver: boolean;
  isDragging: boolean;
  /** The selected person when they are one of this manager's grouped reports. */
  activeGroupedId: string | null;
  searchTerm: string;
  deptColor: string;
  deptLabel: string;
  canEdit: boolean;
  /** The canvas's one keyboard stop (roving tabindex); the other cards are reached with the arrow keys. */
  isTabStop: boolean;
  /** Zoomed out: name and headcount only, in larger type. */
  overview: boolean;
  onSelect: (id: string, via?: SelectVia) => void;
  /** Arrow keys, Home and E on the card (the chart moves focus or opens the editor). */
  onCardKeyDown: (id: string, event: React.KeyboardEvent<HTMLElement>) => void;
  onCardFocus: (id: string) => void;
  onToggle: (id: string) => void;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDrop: (target: Employee) => void;
  onDragEnter: (id: string) => void;
  onDragLeave: () => void;
}

/**
 * One person in the tree (book §11.8 selectable card): white surface, 1px
 * border, 12px radius; selected = 2px Blue 500 + bg.selected. The canvas has
 * one card in the tab order at a time (roving tabindex); that card's team
 * toggle and grouped avatars follow it, the rest are reached with arrow keys.
 */
const OrgNodeCard = React.memo<OrgNodeCardProps>(({
  item,
  densityMode,
  isExpanded,
  isSelected,
  isMatch,
  isContext,
  isDragOver,
  isDragging,
  activeGroupedId,
  searchTerm,
  deptColor,
  deptLabel,
  canEdit,
  isTabStop,
  overview,
  onSelect,
  onCardKeyDown,
  onCardFocus,
  onToggle,
  onDragStart,
  onDragEnd,
  onDrop,
  onDragEnter,
  onDragLeave,
}) => {
  const node = item.data.employee;
  if (!node) return null;

  const isManagerMode = densityMode === 'managers';
  const jobTitle = jobTitleOf(node);
  const title = jobTitle ?? getEmployeeTeamLabel(node);
  // With a real job title, the team moves to the department line; without one, the title line already is the team.
  const showTeamBesideDepartment = Boolean(jobTitle) && Boolean(node.teamName);
  const innerTabIndex = isTabStop ? 0 : -1;
  const groupedReports = item.data.groupedReports;
  const groupedPreview = groupedReports.slice(0, GROUPED_PREVIEW);
  const hiddenGrouped = groupedReports.length - groupedPreview.length;
  const hasChildren = item.data.childCount > 0;
  const directCount = item.data.directCount;
  const total = item.data.totalDescendants;
  const raised = isSelected || isDragOver;
  const surface = raised ? sbg.bg.selected : isContext ? sbg.bg.surfaceSubtle : sbg.bg.surface;
  const borderColor = raised || isMatch ? sbg.border.selected : sbg.border.default;

  const accessibleName = [
    node.name,
    title,
    deptLabel !== title ? deptLabel : null,
    directCount > 0 ? formatCount(directCount, 'direct report') : null,
  ].filter(Boolean).join(', ');

  const stopDrag = (event: React.DragEvent) => {
    if (!canEdit) return;
    event.preventDefault();
  };

  return (
    <Box
      draggable={canEdit}
      onDragStart={(event) => {
        if (!canEdit) return;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', node._id);
        onDragStart(node._id);
      }}
      onDragEnd={canEdit ? onDragEnd : undefined}
      onDragOver={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDragEnter={(event) => {
        stopDrag(event);
        if (canEdit) onDragEnter(node._id);
      }}
      onDragLeave={canEdit ? onDragLeave : undefined}
      onDrop={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        onDrop(node);
      }}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.5,
        borderRadius: sbg.radius.lg,
        bgcolor: surface,
        border: `1px ${isContext && !raised ? 'dashed' : 'solid'} ${borderColor}`,
        boxShadow: raised ? `0 0 0 1px ${sbg.border.selected}` : 'none',
        opacity: isDragging ? 0.5 : 1,
        cursor: canEdit ? 'grab' : 'pointer',
        transition: transition('border-color', 'background-color', 'box-shadow', 'opacity'),
        '&:hover': raised
          ? undefined
          : { borderColor: isMatch ? sbg.border.selected : sbg.border.strong, boxShadow: sbg.shadow.sm },
        '&:active': { cursor: canEdit ? 'grabbing' : 'pointer' },
      }}
    >
      {/* The card's main control; its ::after stretches over the whole card. */}
      <Box
        role="button"
        tabIndex={isTabStop ? 0 : -1}
        data-org-card={node._id}
        aria-pressed={isSelected}
        aria-label={accessibleName}
        onFocus={() => onCardFocus(node._id)}
        onClick={() => onSelect(node._id, 'pointer')}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(node._id, 'keyboard');
            return;
          }
          onCardKeyDown(node._id, event);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 0,
          '&:focus-visible': { outline: 'none' },
          '&::after': { content: '""', position: 'absolute', inset: -1, borderRadius: sbg.radius.lg },
          '&:focus-visible::after': focusRing,
        }}
      >
        {overview ? (
          <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: 1 }}>
            <Typography
              component="div"
              sx={{
                ...typeStyle('heading-3'),
                color: isContext ? sbg.text.secondary : sbg.text.primary,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: isManagerMode ? 2 : 1,
                overflow: 'hidden',
                overflowWrap: 'anywhere',
              }}
            >
              <HighlightText text={node.name} highlight={searchTerm} />
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <DeptDot color={deptColor} size={12} />
              <Typography
                component="span"
                noWrap
                sx={{ ...typeStyle('body-lg'), color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}
              >
                {total > 0 ? `${formatNumber(total)} in org` : deptLabel}
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <PersonAvatar name={node.name} size={32} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="labelMd"
                component="div"
                noWrap
                sx={{ color: isContext ? sbg.text.secondary : sbg.text.primary }}
              >
                <HighlightText text={node.name} highlight={searchTerm} />
              </Typography>
              <Typography variant="body2" component="div" noWrap sx={{ color: sbg.text.secondary }}>
                {title}
              </Typography>
            </Box>
            {directCount > 0 && (
              <Tooltip title={formatCount(directCount, 'direct report')}>
                <Box component="span" sx={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignSelf: 'flex-start' }}>
                  <CountBadge count={directCount} />
                </Box>
              </Tooltip>
            )}
          </>
        )}
      </Box>

      {!overview && (
        <DepartmentLabel color={deptColor} label={deptLabel} team={showTeamBesideDepartment ? node.teamName : null} />
      )}

      {isManagerMode && !overview && (
        <Box
          sx={{
            mt: 'auto',
            pt: 1,
            minHeight: 33,
            boxSizing: 'border-box',
            borderTop: `1px solid ${sbg.border.subtle}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
          }}
        >
          {groupedReports.length > 0 && (
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flex: 'none' }}>
              {groupedPreview.map((report) => {
                const active = activeGroupedId === report._id;
                return (
                  <Tooltip key={report._id} title={report.name}>
                    <ButtonBase
                      draggable={canEdit}
                      tabIndex={innerTabIndex}
                      data-select-person={report._id}
                      aria-label={`Select ${report.name}`}
                      aria-pressed={active}
                      onDragStart={(event) => {
                        if (!canEdit) return;
                        event.stopPropagation();
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', report._id);
                        onDragStart(report._id);
                      }}
                      onDragEnd={canEdit ? onDragEnd : undefined}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect(report._id, viaOf(event));
                      }}
                      sx={{
                        borderRadius: '50%',
                        boxShadow: active ? `0 0 0 2px ${sbg.border.selected}` : 'none',
                        transition: transition('box-shadow'),
                        '&:hover': { boxShadow: `0 0 0 2px ${active ? sbg.border.selected : sbg.border.hover}` },
                        '&.Mui-focusVisible': focusRing,
                      }}
                    >
                      <PersonAvatar name={report.name} size={24} />
                    </ButtonBase>
                  </Tooltip>
                );
              })}
              {hiddenGrouped > 0 && (
                <Tooltip title={`Show all ${formatNumber(groupedReports.length)} in ${node.name}'s team`}>
                  <ButtonBase
                    tabIndex={innerTabIndex}
                    aria-label={`Show all ${formatNumber(groupedReports.length)} people in ${node.name}'s team`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(node._id, viaOf(event));
                    }}
                    sx={{
                      minWidth: 28,
                      height: 24,
                      px: 0.75,
                      borderRadius: sbg.radius.pill,
                      bgcolor: sbg.bg.surfaceSubtle,
                      color: sbg.text.secondary,
                      boxShadow: `inset 0 0 0 1px ${sbg.border.default}`,
                      font: 'var(--sbg-type-label-xs)',
                      fontVariantNumeric: 'tabular-nums',
                      '&:hover': { color: sbg.text.primary, bgcolor: sbg.bg.hover },
                      '&.Mui-focusVisible': focusRing,
                    }}
                  >
                    +{formatNumber(hiddenGrouped)}
                  </ButtonBase>
                </Tooltip>
              )}
            </Box>
          )}
          <Typography
            variant="caption"
            component="span"
            noWrap
            sx={{ ml: 'auto', minWidth: 0, color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}
          >
            {total > 0 ? `${formatNumber(total)} in org` : 'No direct reports'}
          </Typography>
        </Box>
      )}

      {hasChildren && (
        <Tooltip title={isExpanded ? 'Collapse team' : 'Expand team'}>
          <ButtonBase
            tabIndex={innerTabIndex}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.name}'s team`}
            onClick={(event) => {
              event.stopPropagation();
              onToggle(node._id);
            }}
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: -12,
              transform: 'translateX(-50%)',
              zIndex: 1,
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: `1px solid ${isExpanded ? sbg.border.default : sbg.border.strong}`,
              bgcolor: sbg.bg.surface,
              color: isExpanded ? sbg.text.secondary : sbg.text.primary,
              transition: transition('background-color', 'border-color', 'color'),
              '&:hover': { bgcolor: sbg.bg.hover, color: sbg.text.primary, borderColor: sbg.border.hover },
              '&.Mui-focusVisible': focusRing,
            }}
          >
            {isExpanded ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
          </ButtonBase>
        </Tooltip>
      )}
    </Box>
  );
});

OrgNodeCard.displayName = 'OrgNodeCard';

export default OrgNodeCard;
